import { useRef, useState } from "react";
import { Button } from "../../../../components/Button";
import { ColumnsIcon } from "../../../../assets/ColumnsIcon";
import { ColumnSettingsPanel } from "./ColumnSettingsPanel";
import type { DefaultTagDefinition } from "../../../../types/tags";
import { configurableColumns, type PlayersColumnLayout } from "./playersTableColumns";
import { tourAnchor } from "../../../tours/tourAnchors";
import "./ColumnSettingsButton.css";

interface ColumnSettingsButtonProps {
  tagDefinitions: DefaultTagDefinition[];
  layout: PlayersColumnLayout;
  onLayoutChange: (layout: PlayersColumnLayout) => void;
}

/**
 * Toolbar entry point for the column picker.
 *
 * Self-contained, like FilterButton beside it: it owns whether the popover is open, so the
 * players section passes only the layout it already holds. The popover is positioned against this
 * wrapper, which is why the wrapper — not the toolbar — carries `position: relative`.
 */
export const ColumnSettingsButton = ({
  tagDefinitions,
  layout,
  onLayoutChange,
}: ColumnSettingsButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  /*
    Handed to the panel so its outside-click dismissal skips this button. Without it a mousedown
    here closed the panel and the click that followed re-opened it, so the trigger was a one-way
    open: nothing could shut the panel except Escape or a click elsewhere on the page.
  */
  const triggerRef = useRef<HTMLButtonElement>(null);

  /*
    Count only the hidden columns that still exist. A layout can name a category the organization
    has since deleted, and counting those would show a badge for columns nobody can turn back on.
  */
  /*
    Columns that are off by default (School) are left out of the count. Otherwise every coach
    would see "1 hidden" before touching anything, and the number would jump the first time they
    rearranged a different column, because that save writes School into `hidden`.
  */
  const hiddenCount = configurableColumns(tagDefinitions).filter(
    (column) => !column.defaultHidden && layout.hidden.includes(column.key)
  ).length;

  return (
    <div className="column-settings-button">
      <Button
        ref={triggerRef}
        variant="secondary"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={
          hiddenCount > 0 ? `Columns (${hiddenCount} hidden)` : "Columns"
        }
        data-testid="column-settings-button"
        {...tourAnchor("players.columns")}
      >
        <ColumnsIcon />
        Columns
        {hiddenCount > 0 && (
          /*
            Mirrors FilterButton's badge: both tell a coach at a glance that the table is showing
            less than everything, which is otherwise invisible until they go looking for a column.
          */
          <span className="column-settings-button__badge" aria-hidden="true">
            {hiddenCount}
          </span>
        )}
      </Button>
      <ColumnSettingsPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        tagDefinitions={tagDefinitions}
        layout={layout}
        onLayoutChange={onLayoutChange}
      />
    </div>
  );
};
